<template>
    <div id="accessKey" class="access-code container-fluid">
        <div class="card-wrapper row">
            <div class="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 card-parent">
                <div class="card">
                    <section class="card-section card-header text-center">
                        <div v-if="isCoopHeader" class="access-code__logo-wrapper">
                            <Logo
                                class="access-code__animod-logo"
                                cooperation-text
                                style="/*CLS prevention*/ flex-direction: column-reverse"
                            />
                            <Logo v-if="platformLogoSrc" class="access-code__partner-logo" :src="platformLogoSrc" />
                        </div>
                        <Logo v-else class="access-code__animod-logo access-code-logo" />

                        <slot name="header-text">
                            <h2 class="access-code-header">{{ $t('AccessCode.header') }}</h2>
                        </slot>
                    </section>

                    <section class="card-section main">
                        <p>
                            {{ $t('AccessCode.text', { button: $t('AccessCode.redeem') }) }}
                        </p>

                        <Form @submitValid="submitCode" :errors="errors">
                            <div class="form-group form-row align-items-center justify-content-center flex-md-nowrap">
                                <div class="col-xs-12 col-md-auto">
                                    <input
                                        type="text"
                                        v-model="code"
                                        minlength="5"
                                        class="form-control input-gray voucher-input"
                                        :placeholder="$t('Generic.voucherCode')"
                                        required
                                        autofocus
                                        :aria-label="$t('Generic.voucherCode')"
                                        ref="codeInput"
                                    />
                                </div>

                                <div class="col-xs-12 col-md-auto text-center text-md-left">
                                    <button class="btn btn-primary" :disabled="submitting" type="submit">
                                        {{ $t('AccessCode.redeem') }}
                                    </button>
                                </div>
                            </div>

                            <translate tag="small" path="Contact.gdpr" class="form-text text-muted">
                                <nuxt-link slot="link" :to="privacyUrl" target="_blank" class="text-reset">{{
                                    $t('DataProtection.link')
                                }}</nuxt-link>
                            </translate>
                        </Form>
                    </section>

                    <section class="card-section card-footer">
                        <nuxt-link :to="impressumUrl" target="_blank" class="footer-link">{{
                            $t('CheckoutFooter.imprint')
                        }}</nuxt-link>
                        <nuxt-link :to="termsUrl" target="_blank" class="footer-link agb">{{
                            $t('CheckoutFooter.agb')
                        }}</nuxt-link>
                        <nuxt-link :to="privacyUrl" target="_blank" class="footer-link">{{
                            $t('DataProtection.link')
                        }}</nuxt-link>
                    </section>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import getHead from '@/mixins/getHead';
import Logo from '@/components/Logo';
import { ErrorTypes } from '@/models/AjaxError';
import Form from '@/components/Form';

export default {
    name: 'AccessCode',
    mixins: [getHead],
    layout: ({ layout }) => layout,
    head() {
        return this.getHead({
            pageTitle: this.$t('AccessCode.title')
        });
    },
    middleware: [
        context => {
            //since vuex get cleared upon every redirect, cookie stores intermediate unauthorized flag
            //context.isHMR indicates webpack's hot module replacement which should not touch cookie
            if (context.$cookies.getCookie('unauthorized') !== 'true' && !context.isHMR) {
                return context.redirect(context.query.redirect || '/');
            } else {
                context.$cookies.deleteCookie('unauthorized');
            }
        }
    ],
    components: {
        Form,
        Logo
    },
    data() {
        if (this.$isDev) {
            const codes = [
                '2eUEBR',
                '2JzV2n',
                '2QH9WT',
                '3BajXg',
                '3bkuMr',
                '3dQUy3',
                '3ftytK',
                '3QJ9kh',
                '3XvCpA',
                '44yZAk',
                '4B8fMP',
                '4BEPQw',
                '4Hflak'
            ];
        }

        return {
            code: '',
            termsUrl: { name: 'category', params: { category: 'agb' } },
            privacyUrl: { name: 'category', params: { category: 'datenschutz' } },
            impressumUrl: { name: 'category', params: { category: 'impressum' } },
            errors: [],
            submitting: false
        };
    },
    computed: {
        supportPhone() {
            //default page data might also require authorization
            return this.$getSafe(this.$store.state.globalMeta, 'supportPhoneNumber') || '+49 (221) 933 74 100';
        },
        errorType() {
            /**
             * Error details do not distinguish incorrect code from absent code.
             */
            return this.$route.query.errorType;
        },
        platformLogoSrc() {
            return this.$store.state.globalMeta.platformLogoUrl;
        },
        isCoopHeader() {
            return this.$store.state.globalMeta.headerVariant === 'cooperation';
        }
    },
    methods: {
        submitCode() {
            this.$isDev && console.log('this.errorType', this.errorType);

            if (this.errorType === ErrorTypes.multiVoucherCode) {
                this.$store.dispatch('setMultiVoucherCode', this.code);
            } else if (this.errorType === ErrorTypes.accessCode) {
                this.$store.dispatch('setToken', this.code);
            } else if (this.errorType === ErrorTypes.dynamicTargetPlatform) {
                this.processDynamicTargetPlatform();
                return; //no need to reload the page
            } else {
                // Unknown error case
            }
            //Why location.reload? While this.$router.replace won't call nuxtServerInit with translations and meta calls to check if they fail again
            location.reload();
        },
        sendGTMEvent() {
            this.$gtm && this.$gtm.pushPageView('accessCodeForm');
        },
        async processDynamicTargetPlatform() {
            this.submitting = true; // do not use finally to enable submitting. Redirecting is slow and button will be enabled before user gets redirected

            try {
                const { data } = await this.$axios.get(`/shop/redirect?code=${this.code}`);
                if (data.location) {
                    location.href = data.location;
                } else {
                    window.$nuxt.$log(`Unable to get the redirect url for the dynamic platform code: ${this.code}`);
                    throw this.$t('Generic.tryAgain');
                }
            } catch (e) {
                if (e.response && e.response.status === 404) {
                    this.errors = [this.$t('Eingegebener Code kann nicht gefunden werden')];
                } else {
                    this.errors = [e];
                }

                // To prevent user from making ajax requests with error state, enabling submit button only after code change
                this.$refs.codeInput.addEventListener(
                    'input',
                    () => {
                        this.submitting = false;
                    },
                    { once: true }
                );
            }
        }
    },
    beforeMount() {
        this.sendGTMEvent();
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;
@use '@/styles/theming' as *;
@use '@/styles/_mixins';

#accessKey {
    position: fixed;
    width: 100%;
    left: 0;
    top: 0;
    height: 100%;
    overflow-y: auto;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    @include themify(schlemmerauszeit, bofrost) {
        @include mixins.local-image(
            themed('$access-code', 'bg-image-phone-webp'),
            themed('$access-code', 'bg-image-phone')
        );

        @include breakpoints.media-breakpoint-up(sm) {
            @include mixins.local-image(
                themed('$access-code', 'bg-image-phablet-webp'),
                themed('$access-code', 'bg-image-phablet')
            );
        }

        @include breakpoints.media-breakpoint-up(md) {
            @include mixins.local-image(
                themed('$access-code', 'bg-image-tablet-webp'),
                themed('$access-code', 'bg-image-tablet')
            );
        }

        @include breakpoints.media-breakpoint-up(lg) {
            @include mixins.local-image(
                themed('$access-code', 'bg-image-small-webp'),
                themed('$access-code', 'bg-image-small')
            );
        }

        @include breakpoints.media-breakpoint-up(xl) {
            @include mixins.local-image(
                themed('$access-code', 'bg-image-large-webp'),
                themed('$access-code', 'bg-image-large')
            );
        }
    }

    .card-wrapper {
        min-height: 100%;
        justify-content: center;
        align-items: center;

        .card {
            color: var(--сard-color);
            background-color: var(--сard-bg);

            .text-muted {
                color: var(--сard-muted-color) !important;
            }

            .card-section.card-header {
                background-color: var(--сard-header-bg);
                color: var(--сard-header-color);
            }

            box-shadow: 0 3px 6px hsla(0, 0%, 0%, 0.16), 0 3px 6px hsla(0, 0%, 0%, 0.23);
            margin: 1.25rem 0;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            flex-wrap: nowrap;

            .card-section {
                padding: 0.75rem 1.25rem;
                border-bottom: 1px solid hsla(0, 0%, 0%, 0.12);

                &.main {
                    flex: 1 0 auto;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }
            }

            .card-footer {
                background-color: hsla(0, 0%, 8%, 0.75);
                display: flex;
                flex-wrap: wrap;
                justify-content: center;

                .footer-link {
                    padding: 1rem;
                    color: hsl(0, 0%, 80%);
                }
            }
        }
    }

    .access-code-header {
        font-size: 1.5rem;
        margin: 0.625rem auto;
    }

    .form-control {
        line-height: 1;
    }

    .btn.btn-primary {
        font-weight: bold;
        text-transform: uppercase;
    }

    .voucher-input {
        min-width: 200px;
    }

    .card-parent {
        max-width: 630px;
    }

    .access-code-logo {
        display: block;
        margin: 0 auto;
        width: 170px;
        line-height: 100px;
    }

    @include breakpoints.media-breakpoint-down(sm) {
        .btn.btn-primary {
            margin-top: variables.$grid-padding;
        }

        .access-code-logo {
            line-height: 1;
        }
    }
}

.access-code {
    &__logo-wrapper {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-items: center;
    }

    &__animod-logo {
        flex-direction: column-reverse;
        gap: 1rem;
    }

    &__partner-logo {
        width: var(--logo-access-code-width);
        height: var(--logo-access-code-height);
        flex: 1;
    }

    ::v-deep {
        .cooperation-text {
            text-transform: uppercase;
            font-weight: bold;
        }
    }
}
</style>
